import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamOverview = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    role: ''
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/team`, formData);
      toast.success('Team member added successfully!');
      setDialogOpen(false);
      setFormData({ name: '', role: '' });
      fetchTeamMembers();
    } catch (error) {
      console.error('Error adding team member:', error);
      if (error.response?.status === 400) {
        toast.error('A team member with this name already exists');
      } else {
        toast.error('Failed to add team member');
      }
    }
  };

  const handleDeleteClick = (id, e) => {
    e.stopPropagation();
    setMemberToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      await axios.delete(`${API}/team/${memberToDelete}`);
      toast.success('✅ Successfully deleted');
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('⚠️ Deletion failed. Please try again later');
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" data-testid="team-overview-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.2</span>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Work Sans, sans-serif'}}>Team Overview</h1>
              <p className="text-slate-600">Manage your team members and their assignments</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-team-member-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Team Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      data-testid="member-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Enter member name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger data-testid="member-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Drafter">Drafter</SelectItem>
                        <SelectItem value="Soil Engineer">Soil Engineer</SelectItem>
                        <SelectItem value="Structure Engineer">Structure Engineer</SelectItem>
                        <SelectItem value="Principal Engineer">Principal Engineer</SelectItem>
                        <SelectItem value="QS">QS</SelectItem>
                        <SelectItem value="Infra Engineer">Infra Engineer</SelectItem>
                        <SelectItem value="Ars Engineer">Ars Engineer</SelectItem>
                        <SelectItem value="MEP Engineer">MEP Engineer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-member-btn">Add Member</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Team Members Table */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle>All Team Members ({teamMembers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 mb-4">No team members yet. Add your first team member!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Active Projects</TableHead>
                    <TableHead>Pending Tasks</TableHead>
                    <TableHead>Total Contract</TableHead>
                    <TableHead>Total Paid</TableHead>
                    <TableHead>Total Unpaid</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamMembers.map(member => (
                    <TableRow 
                      key={member.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/team/${member.id}`)}
                      data-testid={`member-row-${member.id}`}
                    >
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{member.role}</Badge>
                      </TableCell>
                      <TableCell>{member.activeProjects || 0}</TableCell>
                      <TableCell>
                        <Badge className="bg-orange-100 text-orange-800">
                          {member.totalPendingTasks || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-slate-800">
                        {formatCurrency(member.totalContractValue || 0)}
                      </TableCell>
                      <TableCell className="text-green-600 font-medium">
                        {formatCurrency(member.totalPaid || 0)}
                      </TableCell>
                      <TableCell className="text-orange-600 font-medium">
                        {formatCurrency(member.totalUnpaid || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteClick(member.id, e)}
                          data-testid={`delete-member-${member.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          title="Delete Team Member"
          description="Are you sure you want to delete this team member? Past transactions will remain recorded but will be tagged as (⚠️ Team member record deleted). This action cannot be undone."
        />
      </div>
    </div>
  );
};

export default TeamOverview;